import React from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  List,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { DisplayFilters, FilterMacro } from 'src/utils/filters/display-filters';
import StyledFilledSelect from './StyledFilledSelect';
import { ExpandMore } from '@mui/icons-material';
import NutritionFactFilter from './NutritionFact';

interface Props {
  displayFilters: DisplayFilters;
  selectedFilters: DisplayFilters;
  setSelectedFilters: React.Dispatch<React.SetStateAction<DisplayFilters>>;
}

const NutritionFactsFilter: React.FC<Props> = ({
  displayFilters,
  selectedFilters,
  setSelectedFilters,
}) => {
  const theme = useTheme();

  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const [dropdownIsOpen, setDropdownIsOpen] = React.useState(false);
  const [expandedAccordionPanel, setExpandedAccordionPanel] = React.useState<
    string | false
  >(false);

  const appliedFilters =
    selectedFilters.macros?.filter((macroFilter) => {
      return macroFilter.min > 0 || macroFilter.max;
    }) || [];

  const toggleAccordionPanel =
    (panel: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpandedAccordionPanel(newExpanded ? panel : false);
    };

  const openDropdown = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownIsOpen(true);
  };
  const closeDropdown = () => setDropdownIsOpen(false);

  const resetNutritionFactFilters = () => {
    setSelectedFilters((prevState) => {
      return {
        ...prevState,
        macros: prevState.macros?.map((macro) => {
          return {
            ...macro,
            min: 0,
            max: null,
          };
        }),
      };
    });
  };

  const label = 'Nutritional information';

  return (
    <React.Fragment>
      <FormControl sx={{ width: 'auto' }}>
        <StyledFilledSelect
          labelId="nutritional-facts-select-label"
          displayEmpty
          id="nutritional-facts-select"
          // When on mobile, prevent the Select from opening its Menu and use Drawer instead
          open={!isSmallScreen && dropdownIsOpen}
          onOpen={openDropdown}
          onClose={closeDropdown}
          // This is a bit of a hack to have the "active" background apply when the user has applied a filter. This component
          //   does not use the Select component's value state.
          value={appliedFilters.length ? [true] : []}
          // Enabling `multiple` prevents the popover from auto-closing after expanding the top accordion.
          //   Alternative solutions include stopping event propagation on click, but this is simpler.
          multiple
          label={label}
          renderValue={() => {
            if (!appliedFilters.length) {
              return (
                <div
                  css={{
                    color: appliedFilters.length
                      ? theme.palette.primary.contrastText
                      : undefined,
                  }}
                >
                  {label}
                </div>
              );
            }

            const filterToDisplay = appliedFilters[0];
            const rangeDisplay = filterToDisplay.max
              ? `${filterToDisplay.min}-${filterToDisplay.max}`
              : `>${filterToDisplay.min}`;
            return (
              <div css={{ display: 'flex' }}>
                <div
                  css={{
                    color: theme.palette.primary.contrastText,
                    marginRight: '4px',
                  }}
                >
                  {filterToDisplay.displayName} {rangeDisplay}
                  {filterToDisplay.unit}
                </div>
                {appliedFilters.length > 1 && (
                  <div css={{ color: theme.palette.secondary.contrastText }}>
                    + {appliedFilters.length - 1} more
                  </div>
                )}
              </div>
            );
          }}
        >
          {displayFilters.macros?.map((displayNutritionalFactFilter) => {
            const selectedNutritionalFactFilter = selectedFilters.macros?.find(
              (selectedFilter) =>
                selectedFilter.key === displayNutritionalFactFilter.key,
            );
            if (!selectedNutritionalFactFilter) {
              return null;
            }

            // This is a scoped setter
            const setNutritionalFactFilter: React.Dispatch<
              React.SetStateAction<FilterMacro>
            > = (valueOrUpdater) => {
              setSelectedFilters((prevState) => {
                const updatedMacros = prevState.macros?.map(
                  (prevStateFilter) => {
                    const isFilterToUpdate =
                      prevStateFilter.key === displayNutritionalFactFilter.key;
                    if (!isFilterToUpdate) {
                      return prevStateFilter;
                    }
                    return typeof valueOrUpdater === 'function'
                      ? valueOrUpdater(prevStateFilter)
                      : valueOrUpdater;
                  },
                );

                return { ...prevState, macros: updatedMacros };
              });
            };
            return (
              <Accordion
                key={displayNutritionalFactFilter.key}
                disableGutters
                square
                css={{
                  borderRadius: '6px',
                  padding: '4px 9px',
                  boxShadow: 'inset 0px -1px 0px ' + theme.palette.grey[100],
                }}
                expanded={
                  expandedAccordionPanel === displayNutritionalFactFilter.key
                }
                onChange={toggleAccordionPanel(
                  displayNutritionalFactFilter.key,
                )}
              >
                <AccordionSummary
                  slotProps={{
                    content: {
                      sx: {
                        margin: 0,
                      },
                    },
                  }}
                  css={{
                    minHeight: '35px',
                    padding: '0 8px',
                    '& .MuiAccordionSummary-content': {
                      margin: 0,
                    },
                  }}
                  expandIcon={<ExpandMore />}
                >
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  >
                    {displayNutritionalFactFilter.displayName} (
                    {displayNutritionalFactFilter.unit})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails css={{ padding: '8px', minWidth: '250px' }}>
                  <NutritionFactFilter
                    displayNutritionalFactFilter={displayNutritionalFactFilter}
                    selectedNutritionFactFilter={selectedNutritionalFactFilter}
                    setSelectedNutritionFactFilter={setNutritionalFactFilter}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
          <div css={{ padding: '9px' }}>
            <Button variant="outlined" onClick={resetNutritionFactFilters}>
              Reset
            </Button>
          </div>
        </StyledFilledSelect>
      </FormControl>

      {/* Mobile Drawer: acts as the Select menu on small screens */}
      <Drawer
        anchor="bottom"
        open={isSmallScreen && dropdownIsOpen}
        onClose={closeDropdown}
        ModalProps={{ keepMounted: true }}
        css={{
          zIndex: 2000,
        }}
        slotProps={{
          paper: {
            style: { height: 'auto' },
            sx: {
              borderTopLeftRadius: 10,
              borderTopRightRadius: 10,
            },
          },
        }}
      >
        <Box sx={{ padding: '8px 16px 8px 16px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 1, py: 0.5 }}>
          <List disablePadding sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {displayFilters.macros?.map((displayNutritionalFactFilter) => {
              const selectedNutritionalFactFilter =
                selectedFilters.macros?.find(
                  (selectedFilter) =>
                    selectedFilter.key === displayNutritionalFactFilter.key,
                );
              if (!selectedNutritionalFactFilter) {
                return null;
              }

              // This is a scoped setter
              const setNutritionalFactFilter: React.Dispatch<
                React.SetStateAction<FilterMacro>
              > = (valueOrUpdater) => {
                setSelectedFilters((prevState) => {
                  const updatedMacros = prevState.macros?.map(
                    (prevStateFilter) => {
                      const isFilterToUpdate =
                        prevStateFilter.key ===
                        displayNutritionalFactFilter.key;
                      if (!isFilterToUpdate) {
                        return prevStateFilter;
                      }
                      return typeof valueOrUpdater === 'function'
                        ? valueOrUpdater(prevStateFilter)
                        : valueOrUpdater;
                    },
                  );

                  return { ...prevState, macros: updatedMacros };
                });
              };
              return (
                <Accordion
                  key={displayNutritionalFactFilter.key}
                  disableGutters
                  css={{
                    borderRadius: '6px',
                    padding: '4px 9px',
                    boxShadow: 'inset 0px -1px 0px ' + theme.palette.grey[100],
                  }}
                  expanded={
                    expandedAccordionPanel === displayNutritionalFactFilter.key
                  }
                  onChange={toggleAccordionPanel(
                    displayNutritionalFactFilter.key,
                  )}
                >
                  <AccordionSummary
                    slotProps={{
                      content: {
                        sx: {
                          margin: 0,
                        },
                      },
                    }}
                    css={{
                      minHeight: '28px',
                      padding: '0 8px',
                      '& .MuiAccordionSummary-content': {
                        margin: 0,
                      },
                    }}
                    expandIcon={<ExpandMore />}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      }}
                    >
                      {displayNutritionalFactFilter.displayName} (
                      {displayNutritionalFactFilter.unit})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails css={{ padding: '8px', minWidth: '250px' }}>
                    <NutritionFactFilter
                      displayNutritionalFactFilter={
                        displayNutritionalFactFilter
                      }
                      selectedNutritionFactFilter={
                        selectedNutritionalFactFilter
                      }
                      setSelectedNutritionFactFilter={setNutritionalFactFilter}
                    />
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </List>
        </Box>
        <Divider />
        <Box
          sx={{
            p: 2,
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button variant="outlined" onClick={resetNutritionFactFilters}>
            Reset
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={closeDropdown}>
              Done
            </Button>
          </Box>
        </Box>
      </Drawer>
    </React.Fragment>
  );
};

export default NutritionFactsFilter;
